import React, { useEffect, useRef, useMemo } from 'react';
import Gantt from 'frappe-gantt';
import './frappe-gantt.css';

const addDays = (iso, days) => {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

/**
 * ApqpGanttChart — frappe-gantt wrapper.
 * Renders one Gantt per project. Modifications on this project's Gantt do NOT affect other projects
 * (each project is independent by design: we only pass its own tasks).
 *
 * Props:
 *  - tasks: array of ApqpTask
 *  - viewMode: 'Day' | 'Week'
 *  - onTaskUpdate(taskId, patch): called after drag/resize
 *  - onTaskClick(task): open the edit dialog
 *  - onProgressChange(taskId, progress): called when progress dragged
 */
export default function ApqpGanttChart({ tasks, viewMode = 'Week', onTaskUpdate, onTaskClick, onProgressChange }) {
  const containerRef = useRef(null);
  const ganttRef = useRef(null);

  const ganttTasks = useMemo(() => {
    if (!tasks || tasks.length === 0) return [];
    return tasks
      .filter(t => t.planned_start && t.planned_end)
      .map(t => {
        const isMilestone = t.task_type === 'milestone';
        const start = t.planned_start;
        // frappe-gantt requires end > start; for milestones use start+1 day
        const end = isMilestone ? addDays(start, 1) : t.planned_end;
        return {
          id: t.id,
          name: t.task_type === 'phase' ? `▸ ${t.name}` : (isMilestone ? `◆ ${t.name}` : t.name),
          start,
          end,
          progress: Number(t.progress || 0),
          dependencies: (t.dependencies || []).join(','),
          custom_class: t.task_type === 'phase' ? 'gantt-phase' : (isMilestone ? 'gantt-milestone' : `gantt-status-${(t.status || 'default').toLowerCase().replace(/\s+/g, '-')}`),
          _raw: t,
        };
      });
  }, [tasks]);

  useEffect(() => {
    if (!containerRef.current) return;
    // Safely clear previous chart without touching innerHTML
    while (containerRef.current.firstChild) {
      containerRef.current.removeChild(containerRef.current.firstChild);
    }
    if (ganttTasks.length === 0) {
      ganttRef.current = null;
      return;
    }
    try {
      ganttRef.current = new Gantt(containerRef.current, ganttTasks, {
        view_mode: viewMode,
        language: 'it',
        bar_height: 22,
        bar_corner_radius: 3,
        padding: 14,
        popup_trigger: 'click',
        custom_popup_html: (task) => {
          const raw = task._raw || {};
          const status = raw.status || '—';
          const resp = raw.responsible || '—';
          const notes = raw.notes ? `<div class="popup-note">${escapeHtml(raw.notes)}</div>` : '';
          return `
            <div class="details-container" style="padding:8px 10px;font-family:system-ui;font-size:12px;min-width:220px">
              <div style="font-weight:600;font-size:13px;margin-bottom:4px">${escapeHtml(task.name.replace(/^▸ /, '').replace(/^◆ /, ''))}</div>
              <div><b>Responsabile:</b> ${escapeHtml(resp)}</div>
              <div><b>Stato:</b> ${escapeHtml(status)}</div>
              <div><b>Inizio:</b> ${task.start} · <b>Fine:</b> ${task.end}</div>
              <div><b>Avanzamento:</b> ${Math.round(task.progress || 0)}%</div>
              ${notes}
            </div>`;
        },
        on_click: (task) => onTaskClick && onTaskClick(task._raw),
        on_date_change: (task, start, end) => {
          if (!onTaskUpdate) return;
          const s = toIso(start);
          const e = toIso(end);
          const raw = task._raw || {};
          const patch = { ...raw, planned_start: s, planned_end: e };
          onTaskUpdate(task.id, patch);
        },
        on_progress_change: (task, progress) => {
          if (!onProgressChange) return;
          onProgressChange(task.id, Math.round(progress));
        },
      });
    } catch (err) {
      console.error('Gantt render error:', err);
    }
  }, [ganttTasks, viewMode, onTaskUpdate, onTaskClick, onProgressChange]);

  if (!tasks || tasks.length === 0) {
    return (
      <div className="text-center py-16 text-slate-400 border-2 border-dashed border-slate-200 rounded-lg">
        <p className="text-sm">Nessuna attività nel Gantt.</p>
        <p className="text-xs mt-1">Aggiungi la prima attività dal tab &quot;Attività&quot; o parti da un modello.</p>
      </div>
    );
  }
  if (ganttTasks.length === 0) {
    return (
      <div className="text-center py-16 text-amber-600 border-2 border-dashed border-amber-200 bg-amber-50 rounded-lg">
        <p className="text-sm">Le attività non hanno date di inizio/fine.</p>
        <p className="text-xs mt-1">Imposta <strong>Data inizio</strong> e <strong>Data fine prevista</strong> per vederle nel Gantt.</p>
      </div>
    );
  }

  return (
    <div className="w-full overflow-x-auto border rounded-lg bg-white" data-testid="apqp-gantt-container">
      <div ref={containerRef} className="min-w-[900px]" />
    </div>
  );
}

function toIso(date) {
  if (!date) return null;
  if (typeof date === 'string') return date.slice(0, 10);
  try { return date.toISOString().slice(0, 10); } catch { return null; }
}

function escapeHtml(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
